/**
 * Code-aware identifier splitting for FTS indexing and query normalization.
 *
 * Splits camelCase, PascalCase, snake_case, and SCREAMING_SNAKE identifiers
 * into constituent words while preserving original text for exact matching.
 *
 * Used by:
 * - Index-time: Generates ftsContent column with original + split variants
 * - Query-time: Normalizes FTS queries symmetrically with indexed content
 */

/**
 * Split a single code identifier into constituent words.
 *
 * Examples:
 *   getUserById     → ["get", "User", "By", "Id"]
 *   MCPServer       → ["MCP", "Server"]
 *   OAuth2Client    → ["OAuth", "2", "Client"]
 *   snake_case      → ["snake", "case"]
 *   BK_RRF_WEIGHT   → ["BK", "RRF", "WEIGHT"]
 *   GPT4Tokenizer   → ["GPT", "4", "Tokenizer"]
 *   HTMLParser      → ["HTML", "Parser"]
 *   getHTTPResponse → ["get", "HTTP", "Response"]
 *   express.Router  → ["express", "Router"]
 */
export function splitIdentifier(identifier: string): string[] {
  // Split on underscores and dots first
  const segments = identifier.split(/[_.]/).filter((s) => s.length > 0);

  const words: string[] = [];
  for (const segment of segments) {
    // Split camelCase/PascalCase with acronym awareness
    // Pattern: split between lowercase→uppercase, uppercase→uppercase+lowercase, letter→digit, digit→letter
    const parts = segment
      .replace(/([a-z])([A-Z])/g, '$1\0$2') // camelCase: getUserById → get\0User\0By\0Id
      .replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1\0$2') // Acronym boundary: MCPServer → MCP\0Server
      .replace(/([a-zA-Z])(\d)/g, '$1\0$2') // Letter→digit: OAuth2 → OAuth\02
      .replace(/(\d)([a-zA-Z])/g, '$1\0$2') // Digit→letter: 2Client → 2\0Client
      .split('\0');

    for (const part of parts) {
      if (part.length > 0) {
        words.push(part);
      }
    }
  }

  return words;
}

/**
 * Extract identifiers from text that are candidates for splitting.
 * Matches camelCase, PascalCase, snake_case, SCREAMING_SNAKE, and dotted paths.
 */
const IDENTIFIER_PATTERN = /(?:[a-zA-Z_$][\w$]*(?:\.[\w$]+)*)/g;

/**
 * Check if an identifier benefits from splitting (has mixed case, underscores, or dots).
 */
function isSplittable(identifier: string): boolean {
  // Has uppercase after lowercase (camelCase/PascalCase)
  if (/[a-z][A-Z]/.test(identifier)) return true;
  // Has underscore (snake_case/SCREAMING_SNAKE)
  if (identifier.includes('_')) return true;
  // Has dot (dotted path)
  if (identifier.includes('.')) return true;
  // Has acronym followed by lowercase (HTMLParser)
  if (/[A-Z]{2,}[a-z]/.test(identifier)) return true;
  // Has digit boundary (OAuth2Client)
  if (/[a-zA-Z]\d|\d[a-zA-Z]/.test(identifier)) return true;
  return false;
}

/**
 * Generate split variants for all identifiers found in text.
 * Returns only the split forms (not the original text).
 */
export function extractSplitVariants(text: string): string {
  const seen = new Set<string>();
  const variants: string[] = [];

  for (const match of text.matchAll(IDENTIFIER_PATTERN)) {
    const identifier = match[0];
    // Skip short identifiers and already-seen ones
    if (identifier.length < 3 || seen.has(identifier)) continue;
    seen.add(identifier);

    if (!isSplittable(identifier)) continue;

    const parts = splitIdentifier(identifier);
    if (parts.length > 1) {
      // Add space-separated form: "get User By Id"
      variants.push(parts.join(' '));
      // Add lowercase form: "get user by id"
      const lower = parts.map((p) => p.toLowerCase()).join(' ');
      if (lower !== parts.join(' ').toLowerCase()) {
        variants.push(lower);
      }
    }
  }

  return variants.join(' ');
}

/**
 * Build FTS-optimized content from original content.
 * Returns original content + appended split variants for additive matching.
 * The original text is preserved verbatim so exact symbol lookup still works.
 */
export function buildFtsContent(originalContent: string): string {
  const variants = extractSplitVariants(originalContent);
  if (variants.length === 0) return originalContent;
  return `${originalContent}\n${variants}`;
}

/**
 * Normalize a search query for FTS by expanding code identifiers.
 * Applies the same splitting logic as index-time for symmetric matching.
 */
export function normalizeFtsQuery(query: string): string {
  const variants = extractSplitVariants(query);
  if (variants.length === 0) return query;
  return `${query} ${variants}`;
}
